/**
 * Standardized error UI components.
 *
 * Rule:
 *  - Form validation        → <FieldError>   inline, directly under the input
 *  - Section / data load    → <SectionError> panel with Retry button
 *  - Background / transient → useToast()     (toast — no component needed)
 *  - Global crash           → <ErrorBoundary> full-screen (already wired in App.tsx)
 */
import type { ReactNode } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

/* ── 1. Field validation error ───────────────────────────────────── */
type FieldErrorProps = {
  message?: string | null;
  id?: string;
  className?: string;
};

export function FieldError({ message, id, className }: FieldErrorProps) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className={cn("mt-1 text-xs text-destructive", className)}>
      {message}
    </p>
  );
}

/* ── 2. Section / data-load failure ─────────────────────────────── */
type SectionErrorProps = {
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
  children?: ReactNode;
};

export function SectionError({ message, onRetry, retryLabel = "Try again", className, children }: SectionErrorProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-start gap-3 rounded-2xl border border-destructive/40 bg-destructive/10 px-5 py-4",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" aria-hidden="true" />
        <p className="text-sm text-destructive">{message}</p>
      </div>
      {children}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/30 px-3 py-1.5 text-xs font-medium text-destructive transition hover:bg-destructive/15"
        >
          <RefreshCw className="h-3 w-3" aria-hidden="true" />
          {retryLabel}
        </button>
      )}
    </div>
  );
}

/* ── 3. Skeleton placeholder row ────────────────────────────────── */
type SkeletonRowsProps = {
  rows?: number;
  className?: string;
};

export function SkeletonRows({ rows = 5, className }: SkeletonRowsProps) {
  return (
    <div className={cn("space-y-2", className)} aria-busy="true" aria-label="Loading…">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-8 w-full animate-pulse rounded-lg bg-muted/50"
          style={{ opacity: 1 - i * 0.12 }}
        />
      ))}
    </div>
  );
}

/* ── 4. Data-empty panel ─────────────────────────────────────────── */
type EmptyPanelProps = {
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
};

export function EmptyPanel({ title, description, action, className }: EmptyPanelProps) {
  return (
    <div className={cn("rounded-2xl border border-dashed border-border bg-background/40 p-8 text-center", className)}>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
